import { useEffect, useState } from "react";
import { Download, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, errorMessage } from "@/lib/api";

// Pages load one small JPEG per request (large quotes used to fail behind the proxy as one huge response).
function usePreviewPages(quote) {
  const [state, setState] = useState({ count: 0, urls: [], error: "", pdfUrl: "" });
  useEffect(() => {
    if (!quote) return undefined;
    let alive = true;
    const made = [];
    setState({ count: 0, urls: [], error: "", pdfUrl: "" });
    const fallback = async (e) => {
      try {
        const r = await api.get(`/quotes/${quote.id}/pdf`, { params: { inline: true }, responseType: "blob" });
        const url = URL.createObjectURL(r.data);
        made.push(url);
        if (alive) setState((s) => ({ ...s, pdfUrl: url, error: "" }));
      } catch (e2) {
        if (alive) setState((s) => ({ ...s, error: errorMessage(e2, errorMessage(e, "Could not render the preview")) }));
      }
    };
    (async () => {
      try {
        const { data } = await api.get(`/quotes/${quote.id}/preview`);
        if (!alive) return;
        setState((s) => ({ ...s, count: data.pages }));
        for (let i = 0; i < data.pages && alive; i += 1) {
          const r = await api.get(`/quotes/${quote.id}/preview/${i}`, { responseType: "blob" });
          const url = URL.createObjectURL(r.data);
          made.push(url);
          if (alive) setState((s) => ({ ...s, urls: [...s.urls, url] }));
        }
      } catch (e) {
        if (alive) fallback(e);
      }
    })();
    return () => { alive = false; made.forEach((u) => URL.revokeObjectURL(u)); };
  }, [quote]);
  return state;
}

export function PreviewDialog({ quote, onClose, onDownload }) {
  const { count, urls, error, pdfUrl } = usePreviewPages(quote);
  const loading = !error && !pdfUrl && urls.length < Math.max(count, 1);
  return (
    <Dialog open={!!quote} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="estimate-preview-dialog" className="flex h-[100dvh] w-screen max-w-none flex-col gap-3 rounded-none border-slate-800 bg-[#F2F2F7] p-3 sm:rounded-none xl:h-[92vh] xl:max-w-5xl xl:gap-4 xl:rounded-lg xl:p-5">
        <DialogHeader className="flex-row items-center justify-between gap-3 space-y-0 pr-8">
          <div className="min-w-0">
            <DialogTitle className="font-display text-slate-50">Estimate preview · <span className="whitespace-nowrap">{quote?.number}</span></DialogTitle>
            <DialogDescription className="text-slate-400">
              Exactly what your customer receives{count ? ` · ${count} page${count === 1 ? "" : "s"}` : ""}.
            </DialogDescription>
          </div>
          <button data-testid="preview-download-button" onClick={onDownload} title="Download PDF"
            className="inline-flex shrink-0 items-center gap-2 rounded-full bg-amber-500 px-3 py-2 text-sm font-semibold text-slate-950 transition-colors hover:bg-amber-400 sm:px-4">
            <Download className="h-4 w-4" /> <span className="hidden sm:inline">Download PDF</span>
          </button>
        </DialogHeader>
        <div data-testid="estimate-preview-pages" className="flex-1 space-y-3 overflow-y-auto rounded-xl border border-slate-800 bg-slate-700/40 p-1.5 xl:space-y-5 xl:p-5">
          {!pdfUrl && urls.map((src, i) => (
            <img key={src} data-testid={`estimate-preview-page-${i}`} src={src} alt={`Page ${i + 1}`}
              className="mx-auto w-full bg-white shadow-[0_10px_40px_rgba(0,0,0,0.45)] xl:max-w-[780px]" />
          ))}
          {pdfUrl && <iframe data-testid="estimate-preview-pdf-fallback" title="Estimate PDF" src={pdfUrl} className="h-full w-full rounded-lg bg-white" />}
          {loading && (
            <div data-testid="estimate-preview-loading" className="flex items-center justify-center gap-2 py-10 text-sm text-slate-400">
              <Loader2 className="h-5 w-5 animate-spin" /> {count ? `Loading page ${urls.length + 1} of ${count}…` : "Building the estimate…"}
            </div>
          )}
          {error && <p data-testid="estimate-preview-error" className="p-6 text-sm text-red-400">{error}</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
}
